import { useState } from 'react';
import {
  Alert, Autocomplete, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem,
  Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { LeadSourceConfigDto, LeadSourceType, SaveLeadSourceDto } from '@sr/dto';
import { ErrorBanner } from 'shared/ui/error-banner';
import { LoadingBanner } from 'shared/ui/loading-banner';
import { extractNestJsErrorMessage } from 'shared/api/rtk-query';
import { useSnack } from 'shared/ui/snack';
import { leadSourcesApi } from './api';

const emptyRule = (): SaveLeadSourceDto => ({
  sourceType: LeadSourceType.EMAIL, sourceValue: '', source2Name: '', source2Subject: '', source2template: '',
});

export const LeadSourcesSettings = () => {
  const { data: rows = [], isLoading, error, refetch } = leadSourcesApi.useGetLeadSourcesQuery();
  const [save, { isLoading: saving }] = leadSourcesApi.useSaveLeadSourceMutation();
  const [remove, { isLoading: deleting }] = leadSourcesApi.useDeleteLeadSourceMutation();
  const [editing, setEditing] = useState<{ id?: number; data: SaveLeadSourceDto } | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<LeadSourceConfigDto | null>(null);
  const { data: options, isFetching: loadingOptions, error: optionsError, refetch: refetchOptions } = leadSourcesApi.useGetLeadSourceOptionsQuery(undefined, {
    skip: !editing, refetchOnMountOrArgChange: true,
  });
  const [formError, setFormError] = useState('');
  const { showSuccess, showError } = useSnack();

  const open = (row?: LeadSourceConfigDto) => {
    setFormError('');
    setEditing(row ? { id: row.id, data: {
      sourceType: row.sourceType, sourceValue: row.sourceValue, source2Name: row.source2Name,
      source2Subject: row.source2Subject, source2template: row.source2template,
    } } : { data: emptyRule() });
  };
  const change = (patch: Partial<SaveLeadSourceDto>) => setEditing((current) => current && ({ ...current, data: { ...current.data, ...patch } }));
  const submit = async () => {
    if (!editing) return;
    setFormError('');
    try {
      await save(editing).unwrap();
      setEditing(null);
      showSuccess('Источник сохранён');
    } catch (e) { setFormError(extractNestJsErrorMessage(e)); }
  };
  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await remove(deleteTarget.id).unwrap();
      setDeleteTarget(null);
      showSuccess('Источник удалён');
    } catch (e) { showError(extractNestJsErrorMessage(e)); }
  };

  if (isLoading) return <LoadingBanner />;
  if (error) return <Stack spacing={2}><ErrorBanner errorMessage={extractNestJsErrorMessage(error)} /><Button onClick={() => refetch()}>Повторить загрузку</Button></Stack>;
  const form = editing?.data;
  const email = form?.sourceType === LeadSourceType.EMAIL;
  const emailOptions = [...new Set([...(options?.emails ?? []), ...(editing?.id && email && form?.sourceValue ? [form.sourceValue] : [])])];
  const valid = !!form?.sourceValue.trim() && !!form?.source2Name.trim() && (email
    ? emailOptions.includes(form.sourceValue)
    : /^[+\d\s().-]+$/.test(form?.sourceValue ?? '') && /^\d{7,15}$/.test(form?.sourceValue.replace(/\D/g, '') ?? ''));

  return (
    <Stack spacing={2}>
      <Box display="flex" alignItems="center" justifyContent="space-between" gap={2}>
        <Typography variant="h6">Источники лидов</Typography>
        <Button variant="contained" onClick={() => open()}>Добавить</Button>
      </Box>
      <TableContainer component={Paper}>
        <Table aria-label="Источники лидов">
          <TableHead><TableRow>
            {['Тип источника', 'Номер / email', 'Источник', 'Тема письма', 'Шаблон', 'Действия'].map((label) => <TableCell key={label}>{label}</TableCell>)}
          </TableRow></TableHead>
          <TableBody>
            {!rows.length && <TableRow><TableCell colSpan={6} align="center">Источники пока не добавлены</TableCell></TableRow>}
            {rows.map((row) => <TableRow key={row.id}>
              <TableCell>{row.sourceType === LeadSourceType.EMAIL ? 'Email' : 'Телефон'}</TableCell>
              <TableCell>{row.sourceValue}</TableCell>
              <TableCell>{row.source2Name}</TableCell>
              <TableCell>{row.sourceType === LeadSourceType.EMAIL ? row.source2Subject || 'Любая тема' : '—'}</TableCell>
              <TableCell>{row.sourceType === LeadSourceType.EMAIL ? row.source2template ? 'Задан' : 'Не задан' : '—'}</TableCell>
              <TableCell><Stack direction="row" spacing={1}>
                <Button size="small" onClick={() => open(row)}>Изменить</Button>
                <Button size="small" color="error" onClick={() => setDeleteTarget(row)}>Удалить</Button>
              </Stack></TableCell>
            </TableRow>)}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={!!editing} onClose={() => !saving && setEditing(null)} fullWidth maxWidth="sm">
        <DialogTitle>{editing?.id ? 'Редактировать источник' : 'Добавить источник'}</DialogTitle>
        <Box component="form" onSubmit={(event) => { event.preventDefault(); if (valid && !saving) submit(); }}>
          <DialogContent>
            {form && <Stack spacing={2} sx={{
              pt: 1,
              '& .MuiInputBase-input': { fontFamily: 'inherit', fontSize: '14px' },
              '& .MuiInputLabel-root': { fontFamily: 'inherit' },
              '& .MuiInputBase-inputMultiline': { padding: 0, lineHeight: 1.5 },
              '& .MuiInputBase-multiline': { padding: '10px 12px', alignItems: 'flex-start' },
            }}>
              {formError && <Alert severity="error">{formError}</Alert>}
              <TextField select size="small" InputLabelProps={{ shrink: true }} label="Тип источника" value={form.sourceType} disabled={saving} onChange={(event) => change({
                sourceType: event.target.value as SaveLeadSourceDto['sourceType'], sourceValue: '', source2Subject: '', source2template: '',
              })}>
                <MenuItem value={LeadSourceType.EMAIL}>Email</MenuItem>
                <MenuItem value={LeadSourceType.PHONE}>Телефон</MenuItem>
              </TextField>
              {optionsError && <Alert severity="error" action={<Button onClick={() => refetchOptions()}>Повторить</Button>}>{extractNestJsErrorMessage(optionsError)}</Alert>}
              {email ? <Autocomplete
                key="email"
                options={emailOptions} value={form.sourceValue || null} loading={loadingOptions} disabled={saving}
                noOptionsText="Активные общие почтовые ящики не найдены" loadingText="Загрузка ящиков…"
                onChange={(_, value) => change({ sourceValue: value ?? '' })}
                renderInput={(params) => <TextField {...params} size="small" InputLabelProps={{ ...params.InputLabelProps, shrink: true }} label="Общий почтовый ящик" required helperText="Активные общие ящики из настроек почты" />}
              /> : <Autocomplete
                key="phone"
                freeSolo options={options?.phones ?? []} inputValue={form.sourceValue} disabled={saving}
                onInputChange={(_, value) => change({ sourceValue: value })}
                renderInput={(params) => <TextField {...params} size="small" InputLabelProps={{ ...params.InputLabelProps, shrink: true }} label="Номер телефона" required inputProps={{ ...params.inputProps, maxLength: 255 }} helperText="Выберите номер из лидов или введите свой с кодом страны, например +73832843941" />}
              />}
              <TextField size="small" InputLabelProps={{ shrink: true }} label="Название источника" value={form.source2Name} required disabled={saving} inputProps={{ maxLength: 255 }} onChange={(event) => change({ source2Name: event.target.value })} />
              {email && <>
                <TextField size="small" InputLabelProps={{ shrink: true }} label="Тема письма" value={form.source2Subject} disabled={saving} inputProps={{ maxLength: 255 }} helperText="Сравнивается целиком, без учёта регистра. Если оставить пустой, подходит любая тема письма" onChange={(event) => change({ source2Subject: event.target.value })} />
                <TextField size="small" InputLabelProps={{ shrink: true }} label="Шаблон обработки" value={form.source2template} disabled={saving} multiline minRows={4} maxRows={12} inputProps={{ maxLength: 16000 }} helperText={'Каждое поле — отдельной строкой, например: Телефон: {{phone}} и Почта: {{email}}. Подпись должна совпадать со строкой письма. Без шаблона используются контакты из заголовков письма.'} onChange={(event) => change({ source2template: event.target.value })} />
              </>}
            </Stack>}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditing(null)} disabled={saving}>Отмена</Button>
            <Button type="submit" variant="contained" disabled={!valid || saving}>{saving ? 'Сохранение…' : 'Сохранить'}</Button>
          </DialogActions>
        </Box>
      </Dialog>
      <Dialog open={!!deleteTarget} onClose={() => !deleting && setDeleteTarget(null)}>
        <DialogTitle>Удалить источник?</DialogTitle>
        <DialogContent><Typography>{deleteTarget?.source2Name} ({deleteTarget?.sourceValue})</Typography></DialogContent>
        <DialogActions>
          <Button disabled={deleting} onClick={() => setDeleteTarget(null)}>Отмена</Button>
          <Button color="error" disabled={deleting} onClick={confirmDelete}>Удалить</Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};
